import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../../Config";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TenantDetailsLoading } from "../../../Components/SkeletonLoading";
import { capitalizeFirstLetter } from "../../../helpers";
import { BoxContainer } from "../../../Components/Styles/PageStyles";
import {
  FaCheck,
  FaSpinner,
  FaUtensils,
  FaCoffee,
  FaConciergeBell,
  FaCookieBite
} from "react-icons/fa";
import Images from "../../../Images";

const TenantService = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.UserReducer.user);
  const tenantId = searchParams.get('tenantId');
  const [apiData, setApiData] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [mealSelection, setMealSelection] = useState([]);
  const [selfi, setSelfi] = useState(null);
  const [error, setError] = useState(null);

  const getMealTypeLabel = (type) => {
    const mealMap = {
      [Config.MEAL_TYPE.BREAKFAST]: { label: 'Breakfast', icon: <FaCoffee className="text-orange-500" /> },
      [Config.MEAL_TYPE.LUNCH]: { label: 'Lunch', icon: <FaUtensils className="text-teal-500" /> },
      [Config.MEAL_TYPE.SNACKS]: { label: 'Snacks', icon: <FaCookieBite className="text-blue-500" /> },
      [Config.MEAL_TYPE.DINNER]: { label: 'Dinner', icon: <FaConciergeBell className="text-purple-500" /> }
    };
    return mealMap[type] || { label: 'Unknown', icon: null };
  };

  const getDietPreferenceLabel = (pref) => {
    const dietMap = {
      [Config.DIET_PREFERENCE.VEG]: 'Veg',
      [Config.DIET_PREFERENCE.NON_VEG]: 'Non-Veg'
    };
    return dietMap[pref] || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      [Config.MEAL_CONSUMPTION_STATUS.PENDING]: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      [Config.MEAL_CONSUMPTION_STATUS.CONSUMED]: { label: 'Consumed', className: 'bg-green-100 text-green-800' },
      [Config.MEAL_CONSUMPTION_STATUS.SKIPPED]: { label: 'Skipped', className: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || { label: 'Unknown', className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}>
        {statusInfo.label}
      </span>
    );
  };

  const updateMealStatus = async ({ mealId, status }) => {
    const response = await axios.post(
      `${Config.apiUrl}/food/tenant/meal/status/update`,
      {
        mealId: mealId,
        status: status
      },
      {
        headers: {
          authorization: "Bearer" + " " + user.token,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  };

  const {
    mutate: updateMealStatusMutate,
    isLoading: isUpdatingMeal,
  } = useMutation(updateMealStatus, {
    onSuccess: (res, variables) => {
      if (res.isSuccess) {
        toast.success(res.msg || "Meal status updated successfully");
        setMealSelection(prevMeals =>
          prevMeals.map(meal =>
            meal.id === variables.mealId
              ? { ...meal, status: variables.status }
              : meal
          )
        );
      } else {
        toast.error(res.msg || "Failed to update meal status");
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.msg || "Failed to update meal status");
      console.error("Error updating meal status:", error);
    },
  });


  const handleMealToggle = (mealId, currentStatus) => {
    if (currentStatus === 1) {
      updateMealStatusMutate({
        mealId: mealId,
        status: Config.MEAL_CONSUMPTION_STATUS.CONSUMED
      });
    } else {
      toast.info("This meal has already been " +
        (currentStatus === 2 ? "consumed" : "skipped") +
        " and cannot be changed");
    }
  };

  const fetchFunction = async () => {
    if (!tenantId) {
      toast.error("Tenant ID is required");
      return;
    }

    const response = await axios.get(`${Config.apiUrl}/client/tenant/qrCode/data?tenantId=${tenantId}`, {
      headers: {
        authorization: "Bearer" + " " + user.token,
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
    });
    return response.data;
  };

  const getSuccess = (res) => {
    if (!res.isSuccess) {
      setError(true);
      toast.error(res.msg || "Failed to fetch tenant data");
    } else {
      setApiData(res);
      setTenantData(res?.tenant);
      setMealSelection(res?.mealSelection || []);
      if (res?.tenant?.profilePic) {
        setSelfi(res.tenant.profilePic);
      }

      setError(null);
    }
  };

  const getError = (res) => {
    setError(true);
    toast.error(res?.message || "Failed to fetch tenant data");
    console.error("Error fetching tenant data:", res);
  };

  const {
    isLoading,
    mutate: getListMutate,
    error: mutationError,
    data,
    isRefetching,
  } = useMutation(fetchFunction, {
    onSuccess: getSuccess,
    onError: getError,
  });

  useEffect(() => {
    if (!tenantId) {
      toast.warning("Tenant ID is missing");
      return;
    }
    if (user?.token) {
      getListMutate();
    }
  }, [tenantId, user?.token]);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!tenantId) {
    return (
      <BoxContainer>
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-700">Tenant ID is required</h3>
          <p className="text-gray-500 mt-2">Please provide a valid tenant ID</p>
          <button
            onClick={handleGoBack}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </BoxContainer>
    );
  }

  return (
    <BoxContainer>
      {!error && !isLoading && tenantData && (
        <Wrapper>
          <CardContainer>
            <HeaderWrapper>
              {/* LEFT: Avatar + Info */}
              <ProfileWrapper>
                <AvatarWrapper>
                  <UserImage
                    src={selfi || Images.Avatar}
                    alt={`Tenant Profile`}
                  />
                  {(tenantData?.kycStatus === 5 || tenantData?.kycStatus === 4) && (
                    <VerifiedBadge>
                      <FaCheck className="text-white" size={10} />
                    </VerifiedBadge>
                  )}
                </AvatarWrapper>
                <TenantInfos>
                  <NameWrapper>
                    <TenantName>
                      {capitalizeFirstLetter(tenantData?.tenantName || "Unknown Tenant")}
                    </TenantName>
                  </NameWrapper>
                  <TenantProp>
                    {tenantData?.roomNum ? `#${tenantData.roomNum}` : 'N/A'},
                    {tenantData?.propName || "N/A"}
                  </TenantProp>
                </TenantInfos>
              </ProfileWrapper>
            </HeaderWrapper>

            {/* ================= BODY ================= */}
            <CardBody>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Tenant ID</InfoLabel>
                  <InfoValue>#{tenantId}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Rent</InfoLabel>
                  <InfoValue>₹{tenantData?.rent || 0}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Security Deposit</InfoLabel>
                  <InfoValue>₹{tenantData?.security || 0}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Rental Cycle</InfoLabel>
                  <InfoValue>{tenantData?.rentalCycle || 'N/A'}</InfoValue>
                </InfoItem>

                <InfoItem>
                  <InfoLabel>Agreement Period</InfoLabel>
                  <InfoValue>{tenantData?.agreementPeriod || 'N/A'} months</InfoValue>
                </InfoItem>
              </InfoGrid>
            </CardBody>
          </CardContainer>

          {/* Meal Selection Section */}
          {mealSelection && mealSelection.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 tracking-tight">
                  Meal Selections
                </h3>
                <span className="text-sm text-gray-400 font-medium">
                  {mealSelection.length} Meals
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {mealSelection.map((meal) => {
                  const mealInfo = getMealTypeLabel(meal.mealType);
                  const isPending = meal.status === Config.MEAL_CONSUMPTION_STATUS.PENDING;
                  const isConsumed = meal.status === Config.MEAL_CONSUMPTION_STATUS.CONSUMED;
                  const isSkipped = meal.status === Config.MEAL_CONSUMPTION_STATUS.SKIPPED;

                  return (
                    <div
                      key={meal.id}
                      className={`group relative bg-white border rounded-lg p-5 shadow-sm hover:shadow-md transition-all duration-300 border-gray-200 
                                              `}
                    >
                      {/* Top */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-lg border flex items-center justify-center border-gray-200 bg-gray-50 `}>
                            {mealInfo.icon}
                          </div>

                          <div>
                            <h4 className="text-base font-semibold text-gray-900">
                              {mealInfo.label}
                            </h4>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {moment(meal.mealDate).format('MMM DD, YYYY')}
                            </p>
                          </div>
                        </div>

                        {/* Status Section */}
                        {isPending ? (
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 font-medium">
                              Pending
                            </span>

                            <button
                              onClick={() => handleMealToggle(meal.id, meal.status)}
                              disabled={isUpdatingMeal}
                              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-300 ${isUpdatingMeal ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'
                                }`}
                            >
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 translate-x-1 ${isUpdatingMeal ? 'opacity-50' : ''
                                }`} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {getStatusBadge(meal.status)}
                            {isUpdatingMeal && (
                              <FaSpinner className="animate-spin text-gray-400" />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-100 mb-4"></div>

                      {/* Details */}
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">Diet Preference</span>
                          <span className="text-sm font-medium text-gray-900">
                            {getDietPreferenceLabel(meal.dietPreference)}
                          </span>
                        </div>

                        <div>
                          <p className="text-sm text-gray-500 mb-1">Description</p>
                          <p className="text-sm text-gray-800 leading-relaxed">
                            {meal.mealDescription || "No description available"}
                          </p>
                        </div>

                        {/* Show status change time if consumed */}
                        {isConsumed && meal.updatedAt && (
                          <div className="text-xs text-gray-400 mt-2">
                            Consumed at: {moment(meal.updatedAt).format('hh:mm A')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Wrapper>
      )}
      {(isLoading || isRefetching) && <TenantDetailsLoading />}
      {error && !isLoading && (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-red-600">Failed to load tenant data</h3>
          <button
            onClick={() => getListMutate()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      )}
    </BoxContainer>
  );
};


const Wrapper = tw.div`px-1`;
const CardContainer = tw.div`bg-white rounded-xl shadow-sm border border-gray-200/60 overflow-hidden`;
const HeaderWrapper = tw.div`flex items-center justify-between gap-6 p-5 border-b border-gray-100`;
const ProfileWrapper = tw.div`flex items-center gap-4 z-index`;
const AvatarWrapper = tw.div`flex items-end w-12 h-12`;
const UserImage = tw.img`w-12 h-12 rounded-full shadow-lg`;
const VerifiedBadge = tw.div`rounded-full bg-emerald-500 p-1 flex items-center justify-center -ml-3.5`;
const TenantInfos = tw.div`flex-1 min-w-0`;
const NameWrapper = tw.div`flex items-center gap-3 flex-wrap`;
const TenantName = tw.h2`text-md font-semibold text-gray-900 truncate`;
const TenantProp = tw.p`text-sm text-gray-500 truncate`;
const CardBody = tw.div`p-5`;
const InfoGrid = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6`;
const InfoItem = tw.div``;
const InfoLabel = tw.div`flex items-center gap-2 text-gray-500 text-sm`;
const InfoValue = tw.p`text-base font-semibold text-gray-900`;

export default TenantService;